import { PointOfInterest } from '@/__generated__/graphql';
import { generatePoiDisplayName } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { primary } from '@/theme/colors';
import { Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { usePoiTemplateFields } from '../../hooks/usePoiTemplateFields';
import { ActionMenu } from './ActionMenu';
import { Preview } from './Preview';
import { ReviewStatus } from './ReviewStatus';
import { Card, CardSkeleton, Details, Field, Header } from './styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { ReviewAction } from './ReviewAction';
import { useFeatureFlag } from '@/hooks';

interface SummaryCardProps {
  data: PointOfInterest;
  poiIndex?: number;
}

export const SummaryCard = ({ data, poiIndex }: SummaryCardProps) => {
  const { templateFieldsWithValues, templateOfSelectedPoi } = usePoiTemplateFields({ data });
  const isAITEngineer = useFeatureFlag('ait-engineer');
  const isAPIInspector = useFeatureFlag('api-inspector');

  const poiName = generatePoiDisplayName({
    name: data.name,
    poiId: data.poiId,
    poiType: data.type,
  });

  return (
    <Card>
      <div data-testid="preview-component">
        <Preview data={data} poiIndex={poiIndex} />
      </div>
      <Stack flexGrow="1" gap="0.8rem">
        <Header>
          <Stack direction="row" alignItems="flex-start" gap="1.2rem">
            <Typography variant="h5" color="primary" fontWeight={600}>
              {poiName}
            </Typography>
            {data.isGoverning && (
              <Tooltip title="Governing POI">
                <span>
                  <AbyssIcon iconName="governing-outline" />
                </span>
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" gap="1.2rem" alignItems="center">
            {isAITEngineer && (
              <ReviewStatus pointOfInterest={data} templateOfSelectedPoi={templateOfSelectedPoi} />
            )}
            {isAPIInspector && (
              <ReviewAction pointOfInterest={data} templateOfSelectedPoi={templateOfSelectedPoi} />
            )}
            <ActionMenu pointOfInterest={data} />
          </Stack>
        </Header>
        <Details
          data-testid="details-component"
          columnCount={templateFieldsWithValues.length > 4 ? 2 : 1}
        >
          {templateFieldsWithValues.map((field) => (
            <Field key={field.label}>
              <Typography lineHeight="2.4rem" variant="h6" fontWeight={400} color={primary.gray}>
                {field.label}:
              </Typography>
              <Typography lineHeight="2.4rem" variant="h6" color={primary.independence}>
                {field.value}
              </Typography>
            </Field>
          ))}
        </Details>
      </Stack>
    </Card>
  );
};

export const SummaryCardSkeleton = () => {
  return (
    <CardSkeleton>
      <div
        style={{
          width: '25.6rem',
          height: '16.9rem',
          flexShrink: 0,
        }}
      >
        <Skeleton
          animation="wave"
          variant="rectangular"
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '0.4rem',
          }}
        />
      </div>

      <Stack flexGrow="1" gap="0.8rem">
        <Header>
          <Skeleton animation="wave" variant="text" width="60%" height="3.2rem" />
          <Skeleton
            animation="wave"
            variant="rectangular"
            width="10rem"
            height="2.4rem"
            sx={{ borderRadius: '3.2rem' }}
          />
        </Header>

        <Details columnCount={1}>
          <Field>
            <Skeleton animation="wave" variant="text" width="40%" height="2.4rem" />
            <Skeleton animation="wave" variant="text" width="35%" height="2.4rem" />
          </Field>
          <Field>
            <Skeleton animation="wave" variant="text" width="35%" height="2.4rem" />
            <Skeleton animation="wave" variant="text" width="40%" height="2.4rem" />
          </Field>
          <Field>
            <Skeleton animation="wave" variant="text" width="45%" height="2.4rem" />
            <Skeleton animation="wave" variant="text" width="30%" height="2.4rem" />
          </Field>
          <Field>
            <Skeleton animation="wave" variant="text" width="50%" height="2.4rem" />
            <Skeleton animation="wave" variant="text" width="25%" height="2.4rem" />
          </Field>
        </Details>
      </Stack>
    </CardSkeleton>
  );
};
